import React from 'react';
import { Section, StudentProgress } from '../types';

interface SectionNavProps {
  sections: Section[];
  progress: StudentProgress;
  onSectionSelect: (sectionIndex: number) => void;
  onScrollToFirstUnanswered?: () => void;
}

const SectionNav: React.FC<SectionNavProps> = ({ sections, progress, onSectionSelect, onScrollToFirstUnanswered }) => {
  const getSectionStatus = (sectionIndex: number) => {
    const sectionProgress = progress.sections[sectionIndex];
    if (!sectionProgress) return 'available';

    const answeredQuestions = (sectionProgress?.answers || []).length;
    const totalQuestions = sections[sectionIndex]?.questions.length || 0;

    if (answeredQuestions === totalQuestions && sectionProgress.passed) return 'completed';
    if (answeredQuestions === totalQuestions && !sectionProgress.passed) return 'needs-review';
    if (answeredQuestions > 0) return 'in-progress';
    return 'available';
  };

  const getQuestionProgress = (sectionIndex: number) => {
    const sectionProgress = progress.sections[sectionIndex];
    const section = sections[sectionIndex];
    const totalQuestions = section?.questions.length || 0;

    // Count questions that have meaningful answers (same logic as header)
    const answeredQuestions = sectionProgress?.answers.filter(answer => {
      if (answer.type === 'multiple-choice') {
        return answer.selectedOption !== undefined && answer.selectedOption !== null;
      } else if (answer.type === 'free-response') {
        return answer.freeResponse !== undefined && answer.freeResponse !== null && answer.freeResponse.trim() !== '';
      }
      return false;
    }).length || 0;

    // Calculate visible questions (not already correct)
    const visibleQuestions = section ? section.questions.filter(question => {
      const previousAnswer = sectionProgress?.answers.find(a => a.questionId === question.id);
      return !previousAnswer || !previousAnswer.isCorrect;
    }) : [];

    const visibleAnswered = visibleQuestions.filter(question => {
      const answer = sectionProgress?.answers.find(a => a.questionId === question.id);
      if (!answer) return false;
      if (answer.type === 'multiple-choice') {
        return answer.selectedOption !== undefined && answer.selectedOption !== null;
      } else if (answer.type === 'free-response') {
        return answer.freeResponse !== undefined && answer.freeResponse !== null && answer.freeResponse.trim() !== '';
      }
      return false;
    }).length;

    const alreadyCorrect = totalQuestions - visibleQuestions.length;

    return {
      answered: answeredQuestions,
      total: totalQuestions,
      visibleTotal: visibleQuestions.length,
      visibleAnswered: visibleAnswered,
      alreadyCorrect: alreadyCorrect
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'needs-review':
        return (
          <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'in-progress':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'available':
        return (
          <div className="w-5 h-5 rounded-full border-2 border-gray-400"></div>
        );
      default:
        return null;
    }
  };

  const getStatusColor = (status: string, isActive: boolean) => {
    if (isActive) return 'bg-safety-100 border-safety-500 text-safety-700';

    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
      case 'needs-review':
        return 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100';
      case 'in-progress':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100';
      case 'available':
        return 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-400';
    }
  };

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiz Sections</h3>

      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Overall Progress</span>
          <span>
            {progress.sections.reduce((total, section, index) => {
              const answered = getQuestionProgress(index).answered;
              return total + answered;
            }, 0)} / {sections.reduce((total, section) => total + section.questions.length, 0)}
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${(progress.sections.reduce((total, section, index) => {
                const answered = getQuestionProgress(index).answered;
                return total + answered;
              }, 0) / sections.reduce((total, section) => total + section.questions.length, 0)) * 100}%`
            }}
          />
        </div>
      </div>

      {/* Section List */}
      <div className="space-y-2">
        {sections.map((section, index) => {
          const status = getSectionStatus(index);
          const isActive = index === progress.currentSection;
          const sectionProgress = progress.sections[index];
          const questionProgress = getQuestionProgress(index);

          return (
            <button
              key={section.id}
              onClick={() => onSectionSelect(index)}
              className={`w-full text-left p-3 rounded-lg border transition-colors duration-200 ${getStatusColor(status, isActive)}`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getStatusIcon(status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">
                      Section {index + 1}
                    </span>
                    <span className="text-xs text-gray-500">
                      {questionProgress.answered}/{questionProgress.total}
                    </span>
                  </div>
                  <p className="font-medium truncate">{section.name}</p>
                  <div className="mt-1">
                    {isActive && (
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>
                          {questionProgress.visibleTotal} questions • {questionProgress.visibleAnswered} answered
                          {questionProgress.visibleTotal - questionProgress.visibleAnswered > 0 && (
                            <>
                              <span className="ml-1">•</span>
                              <span
                                className="ml-1 text-blue-600 cursor-pointer hover:text-blue-800 underline"
                                onClick={onScrollToFirstUnanswered}
                              >
                                {questionProgress.visibleTotal - questionProgress.visibleAnswered} unanswered
                              </span>
                            </>
                          )}
                        </div>
                        {questionProgress.alreadyCorrect > 0 && (
                          <div className="text-green-600">
                            ({questionProgress.alreadyCorrect} questions already correct)
                          </div>
                        )}
                      </div>
                    )}
                    {!isActive && (
                      <div className="text-xs text-gray-500">
                        {questionProgress.total} questions
                        {questionProgress.answered < questionProgress.total && (
                          <span className="ml-1 text-orange-600">
                            • {questionProgress.total - questionProgress.answered} unanswered
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SectionNav;